import React from 'react';
import { motion } from 'framer-motion';
import { Star, Mic, Heart, Target, CheckCircle, ArrowLeft, Lightbulb, XCircle, CheckSquare, Zap, Search, Send, HelpCircle, FileText, Video, ExternalLink } from 'lucide-react';
import { StepCard, VideoCard, VideoGrid, InfoCard } from './SectionComponents';

const StandOutSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  const elevatorPitchExample = `"Three things I love that probably aren't healthy: fixing broken systems, juggling messy problems, and launching side projects that spiral out of control. That's why I cut onboarding time from 10 days to 4 at a Series A fintech, built a CRM from scratch at a proptech startup, and grew a food blog to 8K monthly readers just for fun. Your company combines pace, ownership, and real user problems — and I'd love to bring that same energy to help you scale what's working (and fix what's not)."`;

  const elevatorPitchElements = [
    "Who you are professionally — mention your background and your most relevant 1-2 experiences",
    "Key achievements — what are you most proud of?",
    "A personal touch — e.g. a hobby, side project or fun fact that makes you memorable",
    "Why you're here — show how your path has led you to wanting this kind of role at this kind of startup"
  ];

  const elevatorPitchExamples = [
    {
      name: "Annabel – Founder Associate @ Mast",
      url: "https://www.loom.com/share/94deaa38f3244496b557c296d09bd0c0?sid=8e3e94cf-c97a-49b3-98d8-b0a83b02b72f",
      creator: "Watch how she structures her answers clearly and highlights transferable skills from her past experiences."
    },
    {
      name: "Kabir – Founder Associate @ Autogen AI",
      url: "https://www.loom.com/share/e142d4b0baec4289984f34827d4bdb9f?sid=a039ce89-ae73-4e77-97b8-a26f4fae3fef",
      creator: "Watch how he summarises key achievements & learnings and uses stats to back them up."
    },
    {
      name: "Cordi – Founder Associate @ ESG360",
      url: "https://www.loom.com/share/882393fd6adc4532b7737df570e1d3b4?sid=56a2dcc0-d2c1-4a8e-b504-181312118d81",
      creator: "Watch how she demonstrates her motivation by highlighting one highly relevant and specific experience/knowledge."
    }
  ];

  const companyObsessionVideos = [
    {
      name: "Kabir - Autogen AI",
      url: "https://www.loom.com/share/bd3fada5603049a1b9e0093b6257ab39?sid=a2aefe84-9f10-408a-b9df-6a210bd24a8e",
      creator: "Watch how he combines industry knowledge, personal anecdotes, and specific details from spotlight to show his motivation.",
      title: "Kabir_Why Autogen_Sales interview training"
    },
    {
      name: "Milly - Jumpstart", 
      url: "https://www.loom.com/share/2ad7719755074a5792024e094dbb536a?sid=8397c7bc-fdab-4348-8d71-5daf2ea2f711",
      creator: "Watch how she shows understanding & fit with the company's values with 3 clearly structured rationales.",
      title: "Milly_Why Jumpstart_Sales Interview Training_"
    }
  ];

  const standoutTips = [
    {
      title: "Show you've done deep research",
      examples: [
        "Reference a specific blog post, product update or LinkedIn comment from the team",
        "Mention a competitor, market trend or customer insight",
        "Suggest something small they could improve (tactfully)"
      ]
    }
  ];

  const ideaExamples = [
    {
      role: "Commercial focused generalist",
      ideas: [
        "Suggest how you'd identify and reach potential customers",
        "Share cold outreach copy or a scrappy campaign idea",
        "Bring 5 companies you think they should partner with or sell to"
      ]
    },
    {
      role: "Operations focused generalist",
      ideas: [
        "Spot inefficiencies in their user journey or team operations",
        "Suggest simple automations or tools to speed things up",
        "Bring a workflow map for onboarding or internal processes"
      ]
    },
    {
      role: "Marketing focused generalist",
      ideas: [
        "Propose content themes, SEO improvements or event ideas",
        "Suggest low-cost ways to grow awareness or community",
        "Come with 2-3 rough ideas for improving positioning or tone of voice"
      ]
    }
  ];

  const goodQuestions = [
    `"From my research company X seems like the biggest threat as they recently launched feature Y — are you worried about this eating into market share or is there a plan to counter?"`,
    `"From the video it sounds as if the plan is to raise series A, but it also looks like there are no other product verticals in your roadmap as per article Y I found online. Does that mean the goal is to scale revenue growth with new customers over the next 12 months? How will this role contribute to that?"`,
    `"You mentioned in podcast Y last year that you were planning to expand into the US imminently — is there any reason this hasn't happened yet? Seems like for reason XYZ it would be a perfect market."`
  ];

  const avoidQuestions = [
    `"What's your favourite thing about working here?"`,
    `"What was the inspiration behind this startup?"`,
    `"What do you love most about your job?"`,
    `"What's the culture like?"`,
    `"What are the goals for the next 1–2 years?"`,
    `"What are your company values?"`,
    `"What is your biggest challenge at the moment?"`,
    `"What are the top traits you are looking for in this hire?"`,
    `"What are the typical hours/salary?"`
  ];

  const motivationExamples = [
    `"I listened to your founder on the XYZ podcast and was drawn to how you tackled [specific problem]. It really resonated with the experience I had developing XYZ product feature at my current company where I [outline experience]"`,
    `"I think my experience at [XYZ Company] will stand me in really good stead for hitting the ground running in this role as I've learnt so much about figuring things out as I go — testing, learning, and changing tack without slowing down. I know this 'scrappy startup' stage well: limited resources, lots of ambiguity, and the need to iterate quickly. That's where I'm most comfortable and where I've delivered before."`
  ];

  const avoidSaying = [
    "Generic or templated answers — if your answer could be used in 10 different interviews, it's too vague",
    "CV walk-throughs — you're not reading your LinkedIn profile and interviewers will have already seen this!",
    "Lack of examples — back everything up with stories or results",
    "Sounding robotic — be confident and warm. Energy matters!"
  ];

  const checklist = [
    "I've crafted a compelling 45-60 second elevator pitch",
    "I've researched the company thoroughly (recent news, competitors, challenges)",
    "I've prepared 3-5 specific ideas or suggestions for the role",
    "I've thought of thoughtful, research-based questions to ask",
    "I've practiced explaining my motivation beyond just \"wanting to work at a startup\"",
    "I've prepared specific examples that demonstrate adaptability and ownership"
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              How to stand out in your first interview
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Beyond answering questions well, great candidates show initiative, thoughtfulness and energy from day one.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          {/* Overview Section */}
          <StepCard title="Overview">
            <div className="space-y-6">
              <p className="text-xl text-gray-800 leading-relaxed">
                👋 Welcome! Let's walk through how to ace your first interview.
              </p>
              
              {/* Interview Details - Compact horizontal layout */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">Duration:</span>
                    <p className="text-gray-600 text-sm">Typically 30–40 minutes</p>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">Interviewer:</span>
                    <p className="text-gray-600 text-sm">1–2 people (team lead &/or founder)</p>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">Their goal:</span>
                    <p className="text-gray-600 text-sm">Assess if you're a strong, motivated fit</p>
                  </div>
                </div>
              </div>
              
              {/* Navigation Section */}
              <div>
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Your goals:</h4>
                <div className="space-y-2">
                  {[
                    { text: 'Nail your elevator pitch', section: 'goal-1', number: '1' },
                    { text: 'Show you\'re obsessed with the company', section: 'goal-2', number: '2' },
                    { text: 'Show you\'re great fit the role', section: 'goal-3', number: '3' },
                    { text: 'Ask good questions', section: 'goal-4', number: '4' }
                  ].map((goal, i) => (
                    <button
                      key={i}
                      onClick={() => scrollToSection(goal.section)}
                      className="flex items-center space-x-3 w-full text-left p-3 rounded-lg border border-gray-300 hover:bg-[#02B491] hover:text-white hover:border-[#02B491] transition-all duration-200 group bg-white cursor-pointer shadow-sm hover:shadow-md"
                    >
                      <div className="flex-shrink-0 w-6 h-6 bg-gray-100 group-hover:bg-white group-hover:text-[#02B491] rounded-full flex items-center justify-center text-xs font-bold text-gray-700">
                        {goal.number}
                      </div>
                      <span className="text-gray-800 group-hover:text-white font-medium">{goal.text}</span>
                      <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>
          
          <div id="goal-1">
            <StepCard title="🎤 Goal 1: Nail your elevator pitch">
              <div className="space-y-8">
                <p className="text-gray-600 text-lg leading-relaxed">
                  Your pitch should be <span className="font-semibold text-gray-800">short, sharp and focused</span>. Aim for 60-90 seconds covering the key points.
                </p>
                
                <div className="p-6 rounded-2xl border border-blue-200 bg-white transition-all duration-300 hover:shadow-lg hover:shadow-gray-900/5">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">Perfect Example</h4>
                    <p className="text-gray-600 leading-relaxed italic">{elevatorPitchExample}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-6 text-xl">Learn from real examples:</h4>
                  <VideoGrid videos={elevatorPitchExamples} columns={3} />
                </div>
              </div>
            </StepCard>
          </div>

          <div id="goal-2">
            <StepCard title="💡 Goal 2: Show you're obsessed with the company">
              <div className="space-y-8">
                <p className="text-gray-600 text-lg leading-relaxed">
                  Vague lines like <span className="line-through">"I love your mission"</span> won't cut it. 
                  Instead, give <span className="font-semibold text-gray-800">3 specific reasons</span> why you're excited about this company.
                </p>
                
                <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                  <h5 className="font-bold text-gray-900 mb-4 text-lg">That might include:</h5>
                  <div className="space-y-3">
                    {[
                      'A podcast, blog post, or cold email they sent',
                      'A product, customer segment, or GTM angle you find compelling',
                      'A clear link between your past experience and what they\'re selling'
                    ].map((tip, i) => (
                      <div key={i} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-800 leading-relaxed">{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <h5 className="font-bold text-gray-900 mb-3 text-lg">Example:</h5>
                  <p className="text-gray-700 leading-relaxed italic">
                    “I actually received one of your cold emails a few months ago and saved it because the copy was very strong. I remember thinking it could hit even harder if it referenced [sector-specific pain point] — for example, in fintech, even a quick nod to compliance overhead or back-office bloat tends to boost reply rates. I’ve tested similar tweaks in past campaigns and saw open-to-reply jump by 15%. It got me excited about how I could help push things even further.”
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-6 text-xl">Check out these best practice examples:</h4>
                  <VideoGrid videos={companyObsessionVideos} columns={2} />
                </div>
              </div>
            </StepCard>
          </div>

          <div id="goal-3">
            <StepCard title="🎯 Goal 3: Show you’re a great fit for the role">
              <div className="space-y-8">
                <p className="text-gray-600 text-lg leading-relaxed">
                  Make it easy for the interviewer to imagine you doing the work from day one. 
                  How? Follow these two steps.
                </p>
                
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Step 1: Start with the JD</h4>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                      <span className="text-gray-800">Go through the job description to find the key traits they’re looking for.</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                      <span className="text-gray-800">Prepare 2–3 specific examples for each trait from your experience.</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <h5 className="font-bold text-gray-900 mb-4 text-lg">Here’s what that might look like:</h5>
                  <div className="space-y-4">
                    {[
                      {
                        title: "Take the initiative",
                        example: "Spotted a gap in our onboarding process, rewrote the internal checklist, and reduced handover errors by 60%."
                      },
                      {
                        title: "Learn fast", 
                        example: "Taught myself Webflow over a weekend to launch a landing page when engineering was at capacity."
                      },
                      {
                        title: "Handle tough moments",
                        example: "Jumped in to manage customer support during a crisis week. Wrote help docs, triaged tickets, and got backlog down by 80%."
                      }
                    ].map((item, i) => (
                      <div key={i} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-[#02B491] rounded-full flex items-center justify-center mt-1">
                          <span className="text-white font-bold text-xs">→</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">{item.title}</span>
                          <span className="text-gray-600"> → {item.example}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h4 className="font-bold text-gray-900 mb-3 text-lg">Step 2: Go beyond the JD</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Next, work out the spike of the role and come up with tailored ideas as if you’re already on the job.
                  </p>
                  <div className="grid gap-6">
                    {ideaExamples.map((example, i) => (
                      <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <h5 className="font-semibold text-gray-900 mb-3 text-base">{example.role}</h5>
                        <div className="space-y-2">
                          {example.ideas.map((idea, j) => (
                            <div key={j} className="flex items-start space-x-2">
                              <CheckCircle className="w-4 h-4 text-[#02B491] mt-1 flex-shrink-0" />
                              <span className="text-slate-700 text-sm">{idea}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <InfoCard
                  icon={Lightbulb}
                  title="Pro Tip"
                  type="success"
                >
                  <ul className="space-y-2 mt-4 list-disc list-inside text-gray-700">
                    <li>Reference real, specific examples from their website or product</li>
                    <li>Mention a competitor, market trend or customer insight</li>
                    <li>Suggest something small they could improve (tactfully)</li>
                    <li>Consider bringing a slide deck or mock-up, and email them the materials afterwards</li>
                  </ul>
                </InfoCard>
              </div>
            </StepCard>
          </div>

          <div id="goal-4">
            <StepCard title="❓ Goal 4: Ask good questions">
              <div className="space-y-10">
                <p className="text-gray-600 text-lg leading-relaxed">
                  Questions aren't just about getting information — they show <span className="font-semibold text-gray-800">how you think and what you prioritise</span>.
                </p>
                
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <h4 className="font-semibold text-gray-900 text-lg">Great questions (show depth and research)</h4>
                  </div>
                  <div className="space-y-4">
                    {goodQuestions.map((question, i) => (
                      <div key={i} className="pl-4 border-l-2 border-green-200 py-3">
                        <p className="text-gray-700 italic leading-relaxed">{question}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-8">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                    <h4 className="font-semibold text-gray-900 text-lg">Questions to avoid (generic and show little preparation)</h4>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {avoidQuestions.map((question, i) => (
                      <div key={i} className="text-gray-700 leading-relaxed text-base font-semibold">
                        {question}
                      </div>
                    ))}
                    <div className="mt-6 space-y-2 col-span-2">
                      <p className="text-gray-600">Also avoid:</p>
                      <ul className="space-y-1 ml-4 list-disc list-inside">
                        <li>Questions that can be answered on the company website</li>
                        <li>Anything too focused on salary, hours or remote policy (save those for later)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </StepCard>
          </div>
        </div>
      </div>
      
      {hasNext && (
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
          <div className="text-center">
            <button
              onClick={onNext}
              className="inline-flex items-center px-8 py-4 bg-white text-[#02B491] rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border-2 border-[#02B491] hover:shadow-emerald-500/20"
            >
              Continue to: {nextSectionTitle}
              <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandOutSection; 